// src/Pages/Landing/Components/CarruselProfesionales.jsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Typography, Container, Link, Skeleton } from "@mui/material";
import callApi from "../../../services/callApi";

// ==============================================
// CONFIGURACIÓN
// ==============================================
const SEGUNDOS_POR_CARD = 2.5;

// Tamaños de foto COMPACTA
const FOTO_COMPACT_MOBILE = 72;
const FOTO_COMPACT_DESKTOP = 90;

// Tamaños de foto EXPANDIDA
const FOTO_EXPANDED_MOBILE = 126;
const FOTO_EXPANDED_DESKTOP = 158;

// Card width para animación (foto + gap)
const CARD_WIDTH_MOBILE = FOTO_COMPACT_MOBILE + 16;
const CARD_WIDTH_DESKTOP = FOTO_COMPACT_DESKTOP + 16;

// Altura FIJA del contenedor
const CAROUSEL_CONTAINER_HEIGHT_MOBILE = 250;
const CAROUSEL_CONTAINER_HEIGHT_DESKTOP = 290;

// Cantidad de placeholders iniciales
const PLACEHOLDER_COUNT = 20;

// Tiempo de auto-cierre: 5 segundos
const AUTO_CLOSE_DELAY = 7500;

// Breakpoint para mobile
const MOBILE_BREAKPOINT = 900;

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================
const CarruselProfesionales = () => {
  const [profesionales, setProfesionales] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isFixed, setIsFixed] = useState(false); // Solo relevante en PC
  const [imageLoadedStates, setImageLoadedStates] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  
  const carouselTrackRef = useRef(null);
  const animationFrameRef = useRef(null);
  const positionRef = useRef(0);
  const isPausedRef = useRef(false);
  const lastTimeRef = useRef(0);
  const autoCloseTimerRef = useRef(null);

  // Refs para soporte de arrastre (drag) con mouse y táctil
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);
  const lastDragTimeRef = useRef(0);
  const dragVelocityRef = useRef(0);
  const hasDraggedRef = useRef(false);

  // ==============================================
  // DETECCIÓN DE DISPOSITIVO
  // ==============================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ==============================================
  // ARRAY CON DUPLICACIÓN 25% PARA LOOP INFINITO
  // ==============================================
  const displayedItems = useMemo(() => {
    if (profesionales.length === 0) {
      return Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => ({
        isPlaceholder: true,
        index: i,
      }));
    }
    
    const duplicateCount = Math.ceil(profesionales.length * 0.25);
    const duplicatedElements = profesionales.slice(0, duplicateCount);
    
    return [...profesionales, ...duplicatedElements].map((prof, i) => ({
      ...prof,
      isPlaceholder: false,
      displayIndex: i,
      realIndex: i % profesionales.length,
    }));
  }, [profesionales]);

  // Punto de reset para loop infinito
  const resetPoint = useMemo(() => {
    if (profesionales.length === 0) {
      const isMobileView = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
      return PLACEHOLDER_COUNT * (isMobileView ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP);
    }
    const isMobileView = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
    const cardWidth = isMobileView ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
    return profesionales.length * cardWidth;
  }, [profesionales]);

  // ==============================================
  // FETCH PROFESIONALES
  // ==============================================
  useEffect(() => {
    const fetchProfesionales = async () => {
      try {
        const response = await callApi('/agenda/landingListadoProfesionales');
        
        if (response.statusCode === 200 && response.profesionales) {
          setProfesionales(response.profesionales.slice(0, 50)); // ✅ Limitar a 50 profesionales máximo
        }
      } catch (error) {
        console.error('Error fetching profesionales:', error);
      }
    };

    fetchProfesionales();
  }, []);

  // ==============================================
  // ANIMACIÓN INFINITA
  // ==============================================
  useEffect(() => {
    const track = carouselTrackRef.current;
    if (!track) return;

    const isMobileView = window.innerWidth < MOBILE_BREAKPOINT;
    const cardWidth = isMobileView ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
    const pixelsPorSegundo = cardWidth / SEGUNDOS_POR_CARD;

    const animate = (currentTime) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = currentTime;
      }

      const deltaTime = (currentTime - lastTimeRef.current) / 1000;
      lastTimeRef.current = currentTime;

      // 1. Si el usuario está arrastrando, actualizamos el transform directamente
      if (isDraggingRef.current) {
        if (positionRef.current < 0) {
          positionRef.current += resetPoint;
        } else if (positionRef.current >= resetPoint) {
          positionRef.current -= resetPoint;
        }
        track.style.transform = `translateX(-${positionRef.current}px)`;
      } 
      // 2. Si se acaba de soltar con inercia/momentum
      else if (Math.abs(dragVelocityRef.current) > 5) {
        positionRef.current -= dragVelocityRef.current * deltaTime;
        
        // Fricción constante para una desaceleración suave
        const friction = 3.0;
        dragVelocityRef.current *= Math.exp(-friction * deltaTime);

        // Si la velocidad baja del umbral, detenemos la inercia y reanudamos avance normal
        if (Math.abs(dragVelocityRef.current) <= 5) {
          dragVelocityRef.current = 0;
          isPausedRef.current = false;
        }

        if (positionRef.current < 0) {
          positionRef.current += resetPoint;
        } else if (positionRef.current >= resetPoint) {
          positionRef.current -= resetPoint;
        }
        track.style.transform = `translateX(-${positionRef.current}px)`;
      } 
      // 3. Movimiento automático normal si no está pausado
      else if (!isPausedRef.current) {
        dragVelocityRef.current = 0;
        positionRef.current += pixelsPorSegundo * deltaTime;

        if (positionRef.current >= resetPoint) {
          positionRef.current = positionRef.current - resetPoint;
        }

        track.style.transform = `translateX(-${positionRef.current}px)`;
      } 
      // 4. Si está pausado (tarjeta expandida/hover), solo mantenemos el transform actual
      else {
        track.style.transform = `translateX(-${positionRef.current}px)`;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [resetPoint]);

  // ==============================================
  // FUNCIONES DE CONTROL
  // ==============================================
  
  // Cancelar timer de auto-cierre
  const cancelAutoClose = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  // Cerrar todo y reanudar animación
  const closeAll = useCallback(() => {
    cancelAutoClose();
    setExpandedIndex(null);
    setIsFixed(false);
    isPausedRef.current = false;
  }, [cancelAutoClose]);

  // Iniciar timer de auto cierre
  const startAutoCloseTimer = useCallback(() => {
    cancelAutoClose();
    autoCloseTimerRef.current = setTimeout(() => {
      setExpandedIndex(null);
      setIsFixed(false);
      isPausedRef.current = false;
    }, AUTO_CLOSE_DELAY);
  }, [cancelAutoClose]);

  // Verificar si tarjeta está completamente visible
  const isCardFullyVisible = useCallback((index) => {
    const allCards = document.querySelectorAll('.profile-card');
    const targetCard = allCards[index];
    if (!targetCard) return false;
    
    const wrapper = document.querySelector('.carousel-wrapper');
    if (!wrapper) return false;
    
    const cardRect = targetCard.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    
    return cardRect.left >= wrapperRect.left && cardRect.right <= wrapperRect.right;
  }, []);

  // ==============================================
  // EVENT HANDLERS
  // ==============================================

  // Mouse entra (SOLO PC)
  const handleMouseEnter = useCallback((index, isPlaceholder) => {
    if (isPlaceholder || isMobile) return;
    
    // Si hay algo FIJADO, no hacer nada (la fijada manda)
    if (isFixed) return;
    
    // Expandir por hover (sin fijar)
    if (isCardFullyVisible(index)) {
      isPausedRef.current = true;
      setExpandedIndex(index);
    }
  }, [isMobile, isFixed, isCardFullyVisible]);

  // Mouse sale (SOLO PC)
  const handleMouseLeave = useCallback((index, isPlaceholder) => {
    if (isPlaceholder || isMobile) return;
    
    // Si está FIJADO, no cerrar (el timer se encarga)
    if (isFixed) return;
    
    // Cerrar INMEDIATAMENTE si era hover
    if (expandedIndex === index) {
      setExpandedIndex(null);
      isPausedRef.current = false;
    }
  }, [isMobile, isFixed, expandedIndex]);

  // Click (PC y Mobile)
  const handleClick = useCallback((e, index, isPlaceholder) => {
    if (isPlaceholder) return;
    if (hasDraggedRef.current) return; // Si arrastró, no expandimos
    
    // Si es click en el link, dejar que navegue
    if (e.target.closest('.profile-link')) return;
    
    e.stopPropagation();
    
    // Si no está visible, ignorar
    if (!isCardFullyVisible(index)) return;

    if (isMobile) {
      // ========== MOBILE ==========
      // Si ya está expandida, cerrar
      if (expandedIndex === index) {
        closeAll();
      } else {
        // Expandir e iniciar timer auto cierre
        cancelAutoClose();
        isPausedRef.current = true;
        setExpandedIndex(index);
        startAutoCloseTimer();
      }
    } else {
      // ========== PC ==========
      // Si esta misma ya está FIJADA, cerrar (toggle off)
      if (expandedIndex === index && isFixed) {
        closeAll();
      } else {
        // Fijar esta tarjeta (puede ser nueva o la que estaba en hover)
        cancelAutoClose();
        isPausedRef.current = true;
        setExpandedIndex(index);
        setIsFixed(true);
        startAutoCloseTimer();
      }
    }
  }, [isMobile, expandedIndex, isFixed, isCardFullyVisible, closeAll, cancelAutoClose, startAutoCloseTimer]);

  // Click fuera de tarjetas
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedIndex !== null && !e.target.closest('.profile-card')) {
        closeAll();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [expandedIndex, closeAll]);

  // Tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && expandedIndex !== null) {
        closeAll();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [expandedIndex, closeAll]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => cancelAutoClose();
  }, [cancelAutoClose]);

  // Handler para carga de imagen
  const handleImageLoad = useCallback((realIndex) => {
    setImageLoadedStates(prev => ({
      ...prev,
      [realIndex]: true,
    }));
  }, []);

  // ==============================================
  // EVENTOS DE ARRASTRE (DRAG & INERCIA)
  // ==============================================
  const getEventX = (e) => {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientX;
    }
    return e.clientX;
  };

  const handleDragStart = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return; // Solo click izquierdo
    if (e.target.closest('.profile-link')) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    
    const clientX = getEventX(e);
    startXRef.current = clientX;
    lastXRef.current = clientX;
    lastDragTimeRef.current = performance.now();
    dragVelocityRef.current = 0;
    
    isPausedRef.current = true;
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    
    const clientX = getEventX(e);
    const dx = clientX - lastXRef.current;
    
    // Si se mueve más de 5px, se considera arrastre y cerramos las tarjetas expandidas
    if (Math.abs(clientX - startXRef.current) > 5) {
      if (!hasDraggedRef.current) {
        hasDraggedRef.current = true;
        closeAll();
        isPausedRef.current = true;
      }
    }
    
    positionRef.current -= dx;
    
    const now = performance.now();
    const dt = (now - lastDragTimeRef.current) / 1000;
    if (dt > 0) {
      const currentVelocity = dx / dt;
      dragVelocityRef.current = dragVelocityRef.current * 0.7 + currentVelocity * 0.3;
    }
    
    lastXRef.current = clientX;
    lastDragTimeRef.current = now;
  }, [closeAll]);

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    
    const now = performance.now();
    if (now - lastDragTimeRef.current > 100) {
      dragVelocityRef.current = 0;
    }
    
    if (!hasDraggedRef.current && expandedIndex === null) {
      isPausedRef.current = false;
    } 
    else if (hasDraggedRef.current && Math.abs(dragVelocityRef.current) <= 5 && expandedIndex === null) {
      isPausedRef.current = false;
    }
    
    // Limpiamos hasDragged en el siguiente tick para que handleClick pueda leerlo
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 50);
  }, [expandedIndex]);

  // ==============================================
  // RENDER
  // ==============================================
  return (
    <Box 
      sx={{ 
        mt: { xs: 11, md: 18 },
        mb: { xs: 0, md: 0 },
      }}
    >
      <Container maxWidth="lg">
        {/* Título */}
        <Typography
          sx={{
            fontFamily: "Poppins-SemiBold",
            fontSize: { xs: "24px", md: "36px" },
            color: "#141414",
            textAlign: { xs: "center", md: "center" },
            mb: { xs: 4, md: 6 },
          }}
        >
          Profesionales que confían en Clinifica
        </Typography>

        {/* Contenedor ALTURA FIJA */}
        <Box
          sx={{
            height: { 
              xs: `${CAROUSEL_CONTAINER_HEIGHT_MOBILE}px`, 
              md: `${CAROUSEL_CONTAINER_HEIGHT_DESKTOP}px` 
            },
            position: 'relative',
          }}
        >
          {/* Wrapper con fades */}
          <Box
            className="carousel-wrapper"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            sx={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              height: '100%',
              userSelect: 'none', // Previene la selección de texto al arrastrar
            
              // Fade izquierdo
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: { xs: '60px', md: '140px' },
                background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                zIndex: 20,
                pointerEvents: 'none',
              },
              
              // Fade derecho
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: { xs: '60px', md: '140px' },
                background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                zIndex: 20,
                pointerEvents: 'none',
              },
            }}
          >
            {/* Track del carrusel */}
            <Box
              ref={carouselTrackRef}
              sx={{
                display: 'flex',
                gap: '16px',
                width: 'fit-content',
                alignItems: 'flex-start',
                willChange: 'transform',
              }}
            >
              {displayedItems.map((item, index) => {
                const isPlaceholder = item.isPlaceholder;
                const realIndex = isPlaceholder ? index : item.realIndex;
                const isImageLoaded = !isPlaceholder && imageLoadedStates[realIndex];
                const isExpanded = expandedIndex === index;
                
                return (
                  <Box
                    key={`card-${index}`}
                    className="profile-card"
                    onMouseEnter={() => handleMouseEnter(index, isPlaceholder)}
                    onMouseLeave={() => handleMouseLeave(index, isPlaceholder)}
                    onClick={(e) => handleClick(e, index, isPlaceholder)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      background: 'white',
                      transition: 'all 0.4s ease',
                      cursor: isPlaceholder ? 'default' : 'pointer',
                      flexShrink: 0,
                      
                      // Estilos condicionales
                      width: isExpanded 
                        ? 'auto' 
                        : { xs: `${FOTO_COMPACT_MOBILE}px`, md: `${FOTO_COMPACT_DESKTOP}px` },
                      padding: isExpanded ? '16px' : 0,
                      borderRadius: isExpanded ? '20px' : '22px',
                      border: isExpanded ? '2px solid #f0f0f0' : 'none',
                      zIndex: isExpanded ? 100 : 1,
                      
                      // Hover sutil solo en compact
                      '&:hover': {
                        transform: (!isPlaceholder && !isExpanded) ? 'translateY(-2px)' : 'none',
                      },
                    }}
                  >
                    {/* Foto */}
                    <Box
                      sx={{
                        width: isExpanded
                          ? { xs: `${FOTO_EXPANDED_MOBILE}px`, md: `${FOTO_EXPANDED_DESKTOP}px` }
                          : { xs: `${FOTO_COMPACT_MOBILE}px`, md: `${FOTO_COMPACT_DESKTOP}px` },
                        height: isExpanded
                          ? { xs: `${FOTO_EXPANDED_MOBILE}px`, md: `${FOTO_EXPANDED_DESKTOP}px` }
                          : { xs: `${FOTO_COMPACT_MOBILE}px`, md: `${FOTO_COMPACT_DESKTOP}px` },
                        borderRadius: '22px',
                        transition: 'all 0.4s ease',
                        flexShrink: 0,
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Skeleton */}
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          borderRadius: '22px',
                          opacity: isImageLoaded ? 0 : 1,
                          transition: 'opacity 0.3s ease',
                          zIndex: 2,
                        }}
                      />
                      
                      {/* Imagen */}
                      {!isPlaceholder && (
                        <img
                          src={item.foto_url}
                          alt={`${item.nombre} ${item.apellido}`}
                          onLoad={() => handleImageLoad(realIndex)}
                          draggable="false" // Evita que el navegador intente arrastrar la imagen por defecto
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '22px',
                            opacity: isImageLoaded ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            zIndex: 1,
                            pointerEvents: 'none', // Evita interferencia con el arrastre del carrusel
                          }}
                        />
                      )}
                    </Box>

                    {/* Info - solo visible cuando expandido */}
                    {!isPlaceholder && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          overflow: 'hidden',
                          transition: 'all 0.4s ease',
                          
                          maxHeight: isExpanded ? '150px' : 0,
                          opacity: isExpanded ? 1 : 0,
                          marginTop: isExpanded ? '12px' : 0,
                          width: isExpanded 
                            ? { xs: `${FOTO_EXPANDED_MOBILE}px`, md: `${FOTO_EXPANDED_DESKTOP}px` }
                            : 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Quicksand-Bold',
                            fontSize: { xs: '14px', md: '16px' },
                            color: '#0A56A2',
                            mb: 0.5,
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.nombre} {item.apellido}
                        </Typography>
                        
                        <Typography
                          sx={{
                            fontFamily: 'Quicksand-Regular',
                            fontSize: { xs: '11px', md: '13px' },
                            color: '#666',
                            mb: 1.5,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.profesion}
                        </Typography>
                        
                        <Link
                          href={`/pro/${item.url_perfil}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="profile-link"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#4669E8',
                            fontFamily: 'Quicksand-Bold',
                            fontSize: { xs: '11px', md: '13px' },
                            textDecoration: 'none',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap',
                            
                            '&:hover': {
                              color: '#2952CC',
                              gap: '6px',
                            },
                            
                            '&::after': {
                              content: '"↗\uFE0E"',
                              fontSize: { xs: '14px', md: '16px' },
                            },
                          }}
                        >
                          Ver perfil
                        </Link>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CarruselProfesionales;
